window.addEventListener('keyup', e => {
  if (e.key === 'Escape') {
    document.dispatchEvent(new CustomEvent('onEsc'));
  }
});

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.isQuickview = this.closest('quick-view') ? true : false;
    this.addEventListener('change', this.onVariantChange);
  }

  // quickviews repeat the PDP section's ids, so look up related elements within this product (or sticky form) only
  get scopeEl() {
    return this.closest('product-detail, sticky-form') || document;
  }

  getScopedById(id) {
    return this.scopeEl.querySelector(`#${CSS.escape(id)}`);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateDisabledOptions();
    this.updateMasterId();
  }

  updateDisabledOptions() {
    // Mirror the liquid `value.variant == nil` check so disabled states follow client-side selection changes.
    // Match by value, not position, since some selects (e.g. Color in the combined listing sticky form) are skipped
    const selects = Array.from(this.querySelectorAll('select'));
    if (selects.length < 2) return;

    const variants = this.getVariantData();
    if (!Array.isArray(variants) || !variants.length) return;

    selects.forEach(select => {
      const otherValues = selects.filter(el => el !== select).map(el => el.value);

      Array.from(select.options).forEach(option => {
        const exists = variants.some(variant => {
          return variant.options.includes(option.value) && otherValues.every(value => variant.options.includes(value));
        });
        option.disabled = !exists;
      });
    });
  }

  slugify(string) {
    return string.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateOptionsLegend() {
    this.currentVariant.options.forEach((option, index) => {
      const replaceText = this.getOptionNameReplaceText() && this.getOptionNameReplaceText().find(o => option === o.title);
      const sizeInfo = this.getSizeInfoText() && this.getSizeInfoText()[this.slugify(option)];
      const sizeInfoText = sizeInfo ? ` ${sizeInfo.replace(option, '')}` : '';
      const el = this.querySelector(`[data-product-option-active="${index}"]`);
      el ? el.innerText = replaceText ? `${replaceText.replace}${sizeInfoText}` : `${option}${sizeInfoText}` : null;
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });

    if (!this.currentVariant) {
      this.setUnavailable();
      return;
    }

    this.closest('[data-pdp-info]')?.classList.remove('variant-unavailable');

    this.updateOptionsLegend();
    this.updateVariantInput();
    this.renderProductInfo();

    window.publish(PUB_SUB_EVENTS.variantChange, {
      sectionId: this.dataset.section,
      isQuickview: this.isQuickview,
      currentVariant: this.currentVariant,
      el: this,
    });
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return;
    const newMedia = this.scopeEl.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    );
    if (!newMedia) return;
    const parent = newMedia.parentElement;
    parent.prepend(newMedia);
    window.setTimeout(() => { parent.scroll(0, 0) });
  }

  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateSku() {
    const skuEl = this.getScopedById('productSku');
    if (!(this.currentVariant && skuEl)) return;
    skuEl.innerText = 'SKU: ' + this.currentVariant.sku;
    const stickySkuEl = this.getScopedById('productStickySku');
    if (!stickySkuEl) return;
    stickySkuEl.innerText = 'SKU: ' + this.currentVariant.sku;
  }

  updateVariantInput() {
    const productForms = this.scopeEl.querySelectorAll(`#${CSS.escape(`product-form-${this.dataset.section}`)}, #product-form-installment, #product-form-sticky`);
    const bisDescription = this.closest('product-detail')?.querySelector('.product-bis-modal__message');

    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    if (bisDescription) {
      const bisDescriptionVariant = bisDescription.querySelector('[data-variant-title]')
      if (bisDescriptionVariant) {
        bisDescriptionVariant.innerText = this.currentVariant.title;
      } else {
        bisDescription.innerHTML = bisDescription.innerHTML.replace('[variant-title]', `<span data-variant-title>${this.currentVariant.title}</span>`);
      }
    }
  }

  updatePickupAvailability() {
    const pickUpAvailability = this.scopeEl.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant?.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  updateStickyATC(html) {
    const mediaDestination = this.getScopedById('productStickyMedia');
    const mediaSource = html.getElementById('productStickyMedia');
    const priceDestination = this.getScopedById('productStickyPrice');
    const priceSource = html.getElementById('productStickyPrice');
    const titleDestination = this.getScopedById('pdpStickyTitle');
    const titleSource = html.getElementById('pdpStickyTitle');

    if (mediaSource && mediaDestination) mediaDestination.innerHTML = mediaSource.innerHTML;
    if (priceSource && priceDestination) priceDestination.innerHTML = priceSource.innerHTML;
    if (titleSource && titleDestination) titleDestination.innerHTML = titleSource.innerHTML;

    priceDestination?.classList.remove('visibility-hidden');
  }

  renderProductInfo() {
    const sectionId = this.dataset.section;
    window.fetchSectionHtml(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${sectionId}`)
      .then((responseText) => {
        const id = `price-${sectionId}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = this.getScopedById(`price-${this.dataset.section}`);
        const source = html.getElementById(id);

        if (source && destination) destination.innerHTML = source.innerHTML;
        this.toggleAddButton();
        this.updateStickyATC(html);
      });
  }

  toggleAddButton() {
    const addButtons = this.scopeEl.querySelectorAll(`#${CSS.escape(`product-form-${this.dataset.section}`)} [name="add"], sticky-form [data-product-add-to-cart]`);
    const bisHidden = this.dataset.bisHidden === 'true';
    const containerEl = this.closest('product-detail') || this.closest('sticky-form');
    const bisButton = containerEl?.querySelector('.product__bis');

    addButtons.forEach(el => {
      if (this.currentVariant.available) {
        el.removeAttribute('disabled');
        el.classList.remove('hide');
      } else {
        el.setAttribute('disabled', true);
        if (bisButton && !bisHidden) el.classList.add('hide');
      }

      el.textContent = this.currentVariant.available ? window.variantStrings.addToCart : window.variantStrings.soldOut;

      if (!this.currentVariant.available && !bisHidden) {
        bisButton?.classList.remove('hide');
      } else {
        bisButton?.classList.add('hide');
      }
    });
  }

  setUnavailable() {
    this.closest('[data-pdp-info]')?.classList.add('variant-unavailable');
    const addButton = this.getScopedById(`product-form-${this.dataset.section}`)?.querySelector('[name="add"]');
    if (!addButton) return;
    addButton.textContent = window.variantStrings.unavailable;
    this.getScopedById(`price-${this.dataset.section}`)?.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"][data-json="product-variants"]').textContent);
    return this.variantData;
  }

  getOptionNameReplaceText() {
    this.optionNameReplaceText = this.optionNameReplaceText || JSON.parse(this.querySelector('[type="application/json"][data-json="option-name-replace-text"]').textContent);
    return this.optionNameReplaceText;
  }

  getSizeInfoText() {
    this.optionSizeInfoText = this.optionSizeInfoText || JSON.parse(this.querySelector('[type="application/json"][data-json="option-size-info-text"]').textContent);
    return this.optionSizeInfoText;
  }

  updateSetInputs() {
    this.querySelectorAll(':checked').forEach(input => {
      const setInput = this.scopeEl.querySelector(`input[data-form-id="${input.getAttribute('form')}"][name$="id"]`);
      if (setInput) setInput.value = this.currentVariant.id;
    });
  }
};
customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      const inputs = Array.from(fieldset.querySelectorAll('input'));
      return inputs.length === 1 ? inputs[0].value : inputs.length && inputs.find((radio) => radio.checked).value;
    });
  }
};
customElements.define('variant-radios', VariantRadios);

/**
  * Formats money, replicated the implementation of the `money` liquid filters
  * @param {number} moneyValue - The money value
  * @returns {string} The formatted money value
  */
const DEFAULT_CURRENCY_DECIMALS = 2;
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
window.formatMoney =  moneyValue => {
  const template = document.documentElement.dataset.moneyFormat || '{{amount}}';
  const currency = document.documentElement.dataset.currency || '';

  return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
    if (typeof placeholder !== 'string') return '';
    if (placeholder === 'currency') return currency;

    let thousandsSeparator = ',';
    let decimalSeparator = '.';
    let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

    if (placeholder === 'amount') {
      // Check first since it's the most common, use defaults.
    } else if (placeholder === 'amount_no_decimals') {
      precision = 0;
    } else if (placeholder === 'amount_with_comma_separator') {
      thousandsSeparator = '.';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      // Weirdly, this is correct. It uses amount_with_comma_separator's
      // behaviour but removes decimals, resulting in an unintuitive
      // output that can't possibly include commas, despite the name.
      thousandsSeparator = '.';
      precision = 0;
    } else if (placeholder === 'amount_no_decimals_with_space_separator') {
      thousandsSeparator = ' ';
      precision = 0;
    } else if (placeholder === 'amount_with_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_with_period_and_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = '.';
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      thousandsSeparator = "'";
      decimalSeparator = '.';
    }

    return window.formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
  });
}

/**
  * Formats money in cents
  * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
  * @param {string} thousandsSeparator - The thousands separator
  * @param {string} decimalSeparator - The decimal separator
  * @param {number} precision - The precision
  * @returns {string} The formatted money value
  */
window.formatCents = (moneyValue, thousandsSeparator, decimalSeparator, precision) => {
  const roundedNumber = (moneyValue / 100).toFixed(precision);

  let [a, b] = roundedNumber.split('.');
  if (!a) a = '0';
  if (!b) b = '';

  // Split by groups of 3 digits
  a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

  return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
}


/* ----- globals ----- */
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function lockScrollEnable() {
  window.menuToggling = true;
  window.pageY = window.scrollY;
  document.body.style.left = 0;
  document.body.style.right = 'var(--scrollbar-width)';
  document.body.style.top = `-${window.pageY}px`;
  document.body.style.position = 'fixed';
}

function lockScrollDisable() {
  document.body.style.position = null;
  document.body.style.left = null;
  document.body.style.right = null;
  document.body.style.top = null;
  window.scrollTo(0, window.pageY);
  window.menuToggling = false;
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

function scrollToEl(el) {
  if (!el) return;
  const announcementHeight = document.querySelector('.shopify-section--announcement')?.offsetHeight || 0;
  const navHeight = document.querySelector('.shopify-section--header')?.offsetHeight || 0;
  const offset = navHeight + announcementHeight;
  const bodyRect = document.body.getBoundingClientRect().top;
  const targetRect = el.getBoundingClientRect().top;
  const targetPosition = targetRect - bodyRect - offset;
  window.scrollTo({ top: targetPosition, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
}

const trapFocusHandlers = {};

// function trapFocus(container, elementToFocus = container) {
function trapFocus(options) {
  let { container, elements = null, elementToFocus = null } = options;
  elementToFocus = elementToFocus || container
  elements = elements || getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; /* If not TAB key */
    /* On the last focusable element and tab forward, focus the first element. */
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    /*  On the first focusable element and tab backward, focus the last element. */
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);
  if (elementToFocus) elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        const idInput = this.form.querySelector('[name=id]');
        if (idInput) idInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.addEventListener('click', this.onClick.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton?.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (this.submitButton) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
        }
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton && this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              fetch(`${routes.cart_url}.js`)
                .then(response => response.json())
                .then(data => {
                  publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form-cart-total',
                    cartData: data,
                  });
                });
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
            this.error = false;

            publish(PUB_SUB_EVENTS.renderCartDrawer, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
            this.cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.submitButton) this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && this.submitButton) this.submitButton.removeAttribute('aria-disabled');
            this.closest('.quickadd--loading')?.classList.remove('quickadd--loading');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      onClick(event) {
        // if this is BIS button, open the BIS modal
        if (!event.target.closest('[data-bis-button]')) return;
        this.onBisButtonClick();
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      onBisButtonClick() {
        const containerEl = this.closest('product-detail') || document
        const modal = containerEl?.querySelector('[data-bis-dialog-el]');
        if (!modal) return;

        modal.showModal();
        document.documentElement.classList.toggle('bis-modal-active');
        document.documentElement.classList.toggle('bis-modal-transition');
        setTimeout(() => {
          document.documentElement.classList.remove('bis-modal-transition');
        }, 300);
      }
    }
  );
}

/* quantity-input used in product forms and cart items */
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

const onScroll = () => {
  if (window.menuToggling) return;

  if (window.pageYOffset > window.headerScrolledThreshold) {
    document.documentElement.classList.add('header--scrolled');
  } else {
    document.documentElement.classList.remove('header--scrolled');
  }

  if (window.pageYOffset > 300 + window.headerScrolledThreshold) {
    document.documentElement.classList.add('header--scrolled--after');
  } else {
    document.documentElement.classList.remove('header--scrolled--after');
  }

  const scrollTop = window.pageYOffset;

  /* scroll direction */
  window.scrollDirection = scrollTop > lastScrollTop ? 'down' : 'up';
  document.documentElement.dataset.scrollDirection = window.scrollDirection;
  lastScrollTop = window.pageYOffset;
};

const inViewVideo = (el, src) => {
  const $video = el.closest('video');

  if (!$video) return;
  if (typeof src !== 'undefined') $video.src = el.dataset.inViewVideoSrc;

  if (!$video.hasAttribute('data-autoplay')) return;
  $video.play();
};

(() => {
  const loading = new Map();

  function load(obj) {
    if (!obj.src) return Promise.resolve();
    if (loading.has(obj.src)) return loading.get(obj.src);

    const promise = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.dataset.inViewScriptName = obj.src;
      el.type = obj.type || 'text/javascript';
      if (obj.defer) el.defer = true;
      else if (obj.async) el.async = true;
      el.addEventListener('load', () => resolve(), { once: true });
      el.addEventListener('error', () => reject(new Error(`lazyLoadScript: failed to load ${obj.src}`)), { once: true });
      el.src = obj.src;
      document.head.appendChild(el);
    });

    loading.set(obj.src, promise);
    promise.catch(() => loading.delete(obj.src));
    return promise;
  }

  window.lazyLoadScript = obj => {
    const cb = obj.callback && typeof window[obj.callback] === 'function' ? window[obj.callback] : null;
    load(obj).then(() => cb && cb()).catch(err => console.error(err));
  };
})();

const inViewScript = obj => {
  lazyLoadScript(obj);
};

document.addEventListener('click', event => {
  /* quickview.js loads when a card scrolls into view, catch clicks that land before <quick-view> is defined */
  const openEl = event.target.closest('[data-quickview-open]');
  if (!openEl || customElements.get('quick-view')) return;

  const quickview = openEl.closest('quick-view');
  if (!quickview?.dataset.inViewScript) return;

  openEl.classList.add('loading');
  openEl.setAttribute('aria-busy', 'true');
  lazyLoadScript(JSON.parse(quickview.dataset.inViewScript));
  customElements.whenDefined('quick-view').then(() => quickview.open());
});

window.renderAfterpayPlacements = root => {
  /* the afterpay script only fills .afterpay-slot on page load, so swap in the block's prerendered placement
     whenever product content is injected later (quickview, swatch change) */
  root.querySelectorAll('[data-afterpay-placement]').forEach(template => {
    template.parentElement.querySelectorAll('.afterpay-slot, square-placement').forEach(el => el.remove());
    template.replaceWith(template.content.cloneNode(true));
  });
};

window.updateAfterpayPlacements = (root, variant) => {
  /* square-placement re-renders when data-amount changes */
  if (!variant) return;
  root.querySelectorAll('square-placement').forEach(el => {
    el.dataset.amount = variant.price / 100;
    if (variant.sku) el.dataset.itemSkus = variant.sku;
  });
};

window.activateSmartWishlist = root => {
  /* Smart Wishlist only renders into the first #smartwishlist on the page (jQuery id selector) and keeps the
     product in globals, so give the live id to the visible product (PDP or open quickview) and re-render it */
  const target = root?.querySelector('[data-smartwishlist]');
  // already live (e.g. reopening the same quickview), skip the reload: each one adds another vendor polling interval
  if (!target || (target.id === 'smartwishlist' && target.querySelector('#bookmarkit'))) return;

  document.querySelectorAll('[data-smartwishlist]').forEach(el => {
    if (el === target) return;
    // clear the parked button too, its #bookmarkit etc ids would otherwise be picked up first
    el.removeAttribute('id');
    el.innerHTML = '';
  });

  target.id = 'smartwishlist';
  if (typeof window.ReloadSmartWishlist === 'function') window.ReloadSmartWishlist();
};

window.loadCustomizerJs = () => {
  /* callback after vue loads, init is idempotent so PDP and every quickview can call it */
  requestAnimationFrame(() => {
    lazyLoadScript({
      src: window.assetUrls['product-customizer.js'],
      callback: 'initProductCustomizers'
    });
  });
};

window.loadSwiper = () => {
  /* load swiper if needed */
  if (!window.swiperLoaded && typeof Swiper !== 'function') {
    const $script = document.createElement('script');
    $script.async = true;
    $script.src = window.assetUrls['swiper.js'];
    document.head.appendChild($script);
    window.swiperLoaded = true
  }

  const swiperIsReady = new Promise(resolve => {
    let timer = setInterval(() => {
      if (typeof Swiper === 'function') {
        clearInterval(timer);
        return resolve();
      }
    }, 30)
  });

  swiperIsReady.then(() => {
    window.swipers();
  }).catch(error => console.error(error))
};

window.publishProductsData = data => {
  /* USAGE:
    window.addEventListener('publishProductsData', data => {
      console.log('<gtm-helper> publishProductsData listener', data.detail);
    });
  */
  window.dispatchEvent(new CustomEvent('publishProductsData', {
    detail: data,
  }));
};

/* swipers init via data-swiper={options} */
window.swiperEls = [];
window.swipers = () => {
  document.querySelectorAll('[data-swiper]:not(.swiper-initialized)').forEach((el, index) => {
    el.dataset.swiperIndex = index;

    window.swiperEls[index] = new Swiper(el, Object.assign({}, {
      a11y: true,
      observer: true,
      slidesPerView: 'auto',
      threshold: 8,
      on: {
        afterInit: s => {
          if (!(s.params.centeredSlides && s.params.loop)) return;
          // Fix bug in Swiper > 11 with loop + insufficient number of centered slides
          s.slideToLoop(1, 0, false);
          s.slideToLoop(0, 0, false);
          s.el.classList.remove('visibility');
        },
      },
    }, JSON.parse(el.dataset.swiper)));

    const changeEvent = el.getAttribute('data-swiper-slide-change');
    const pauseVideos = () => {
      el.querySelectorAll('video:not([data-autoplay])').forEach(video => {
        if (!video.paused) video.pause();
      });
    };

    if (changeEvent && window.hasOwnProperty(changeEvent) && typeof window[changeEvent] === 'function') {
      window.swiperEls[index].on('slideChange', function(e) {
        window[changeEvent](e);
        pauseVideos();
      })
    } else {
      window.swiperEls[index].on('slideChange', () => {
        pauseVideos();
      });
    }
  });

  window.swiperInitalized = true;
  window.publish(PUB_SUB_EVENTS.swiperInit, {});
};

(() => {
  // one request per section url per page view, so color prefetches and the price + gallery updates share it
  const cache = new Map();

  window.fetchSectionHtml = url => {
    const key = new URL(url, window.location.origin).href;

    if (!cache.has(key)) {
      const request = fetch(key)
        .then(response => {
          if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
          return response.text();
        })
        .catch(error => {
          cache.delete(key);
          throw error;
        });

      cache.set(key, request);
    }

    return cache.get(key);
  };
})();

window.inView = () => {
  /* add .in-view class to [data-in-view] els as they enter the viewport */
  const inViewEls = document.querySelectorAll('[data-in-view]');

  const inViewObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in-view');

        if (entry.target.hasAttribute('data-autoplay') || entry.target.hasAttribute('data-in-view-video-src')) {
          inViewVideo(entry.target, entry.target.dataset.inViewVideoSrc);
        }

        if (entry.target.hasAttribute('data-in-view-swiper')) {
          loadSwiper();
          inViewObserver.unobserve(entry.target);
        }

        if (entry.target.hasAttribute('data-in-view-script')) {
          inViewScript(JSON.parse(entry.target.dataset.inViewScript));
        }
      } else {
        if (entry.target.hasAttribute('data-autoplay') && entry.target.paused == false) {
          entry.target.pause()
        }
      }
    });
  });

  inViewEls.forEach(el => inViewObserver.observe(el));
};

document.addEventListener('onEsc', () => {
  const expandedCards = document.querySelectorAll('[data-product-card].show-quickadd');
  expandedCards.forEach(card => {
    card.classList.remove('show-quickadd');
    const actionBtn = card.querySelector('[data-quickadd-action-btn]');
    if (actionBtn) {
      actionBtn.setAttribute('aria-expanded', 'false');
      actionBtn.focus();
    }
  });
});

document.addEventListener('DOMContentLoaded', () => {
  window.lastScrollTop = window.pageYOffset;

  inView();
  onScroll();

  const loadLazyElsEarly = () => {
    // Instead of waiting to get scrolled into view, start loading lazy elements when page is idle
    if (typeof window.requestIdleCallback !== 'function') return;

    const hasModalToggleEl = document.querySelector('modal-toggle') !== null;
    const hasRecentlyViewed = document.querySelector('recently-viewed') !== null;
    if (!(hasRecentlyViewed || hasModalToggleEl)) return;

    window.requestIdleCallback(deadline => {
      while (deadline.timeRemaining() > 0) {
        /* load recs early to fix potential layout shifts */
        if (hasRecentlyViewed) lazyLoadScript({ src: window.assetUrls['recently-viewed.js'] });

        /* ensure clicking modal toggles before fully scrolled into view works as expected */
        if (hasModalToggleEl) lazyLoadScript({ src: window.assetUrls['modal-toggle.js'] });
      }
    }, { timeout: 5000 });
  }

  loadLazyElsEarly();

  window.addEventListener('scroll', () => {
    onScroll();
  }, { passive: true });
});
